import React, { useEffect, useRef, useState } from 'react';
import Search from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import SuggestedLocationDropdown from 'components/suggested-location-dropdown';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import styles from './style.module.scss';

import type SearchPrediction from 'data/search-predictions';
import type { SearchAgentPrediction } from 'components/suggested-location-dropdown';

export interface SearchPredictionInputProps {
  // Core search functionality
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isLoadingSearchPredictions: boolean;
  searchPredictions: SearchPrediction[];
  searchAgentPredictions?: SearchAgentPrediction[];
  isTyping: boolean;
  handlePredictionClick: (prediction: SearchPrediction) => void;
  
  // Optional configuration
  placeholder?: string;
  autoFocus?: boolean;
  mobile?: boolean;
  
  // Event handlers
  onSubmit?: (e?: React.FormEvent) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  
  // Override styling (use sparingly - most styling is now internal)
  className?: string;
}

const DROPDOWN_TRIGGER_LENGTH = 3;

export default function SearchPredictionInput({
  searchQuery,
  setSearchQuery,
  isLoadingSearchPredictions,
  searchPredictions,
  searchAgentPredictions = [],
  isTyping,
  handlePredictionClick,
  placeholder = 'Search by address, city, state, or zip',
  autoFocus = false,
  mobile = false,
  onSubmit,
  onKeyDown,
  className = '',
}: SearchPredictionInputProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const [predictionJustClicked, setPredictionJustClicked] = useState(false);

  // Auto-focus if requested
  useEffect(() => {
    if (autoFocus && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [autoFocus]);

  // Control dropdown visibility based on query length and search state
  useEffect(() => {
    // Don't show dropdown if a prediction was just clicked
    if (predictionJustClicked) {
      setIsDropdownActive(false);
      return;
    }
    
    if (searchQuery.length >= DROPDOWN_TRIGGER_LENGTH && !isLoadingSearchPredictions && searchPredictions.length > 0) {
      setIsDropdownActive(true);
    } else {
      setIsDropdownActive(false);
    }
  }, [searchQuery, isLoadingSearchPredictions, searchPredictions.length, predictionJustClicked]);

  // Reset predictionJustClicked flag when user starts typing again
  useEffect(() => {
    if (searchQuery.length >= DROPDOWN_TRIGGER_LENGTH) {
      setPredictionJustClicked(false);
    }
  }, [searchQuery]);

  const dismissDropdown = () => {
    setIsDropdownActive(false);
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    onSubmit?.(e);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
  };

  const wrappedHandlePredictionClick = (prediction: SearchPrediction) => {
    setPredictionJustClicked(true);
    handlePredictionClick(prediction);
  };

  const containerClasses = `${mobile ? styles.mobile : ''} ${className}`.trim();

  return (
    <form className={`${styles.searchForm} ${containerClasses}`} onSubmit={handleSearch} role="search">
      <div className={styles.searchContainer} ref={useOutsideClickHandler(dismissDropdown)}>
        <input
          ref={searchInputRef}
          className={styles.searchInput}
          type="text"
          name="search"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
        />
        <button className={styles.searchButton} type="submit" aria-label="Search">
          <Search />
        </button>
        
        {/* Search Predictions Dropdown */}
        {(!isLoadingSearchPredictions && isDropdownActive && searchQuery.length >= DROPDOWN_TRIGGER_LENGTH) && (
          <div className={styles.predictionsDropdown}>
            <SuggestedLocationDropdown
              searchPredictions={searchPredictions}
              setActivePrediction={() => {}} // eslint-disable-line @typescript-eslint/no-empty-function
              isActive={isDropdownActive}
              isLoading={isLoadingSearchPredictions}
              onClick={wrappedHandlePredictionClick}
              isTyping={isTyping}
              searchAgentPredictions={searchAgentPredictions}
              fullWidth
              className={styles.suggestedLocationDropdown}
            />
          </div>
        )}
        
        {/* Show loading indicator */}
        {(searchQuery.length >= DROPDOWN_TRIGGER_LENGTH && isLoadingSearchPredictions) && (
          <div className={styles.predictionsDropdown}>
            <div className={styles.loadingIndicator}>
              <CircularProgress size={16} color="inherit" />
              <span>Searching...</span>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
